import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { getTradeTheme } from '../../constants/tradeTheme';
import { trackEvent } from '../../services/api/analyticsService';
import { SEARCH_TRADE_IDS, TRADE_EXAMPLE_JOBS } from '../../constants/serviceSearchCatalog';
import {
  buildSomethingElsePath,
  buildTaskStepPath,
  getSearchTradeLabel,
} from '../../utils/serviceSearch';
import { TileHint, TileIcon, TileLabel, TileRow, TradeTile } from './TradeTiles.styled';

const SOMETHING_ELSE_COLOR = '#475569';

/**
 * Browse-by-trade entry points for service search, plus "Something else".
 * `icon` = large icon-over-label tiles (home page); `pill` = compact chips under a search field.
 */
const TradeTiles = ({ variant = 'pill', note = '' }) => {
  const navigate = useNavigate();

  const tiles = [
    ...SEARCH_TRADE_IDS.map((tradeId) => {
      const theme = getTradeTheme(tradeId);
      return {
        key: tradeId,
        label: getSearchTradeLabel(tradeId),
        Icon: theme.icon,
        color: theme.color,
        path: buildTaskStepPath(tradeId, { note }),
        serviceShortName: tradeId,
      };
    }),
    {
      key: 'other',
      label: 'Something else',
      Icon: MoreHorizRoundedIcon,
      color: SOMETHING_ELSE_COLOR,
      path: buildSomethingElsePath({ note }),
    },
  ];

  return (
    <TileRow $variant={variant} role="list" aria-label="Browse by trade">
      {tiles.map(({ key, label, Icon, color, path, serviceShortName }) => (
        <TradeTile
          key={key}
          $variant={variant}
          role="listitem"
          onClick={() => {
            if (serviceShortName) {
              trackEvent({ eventType: 'service_view', businessId: 'app', serviceShortName });
            }
            navigate(path);
          }}
        >
          <TileIcon $variant={variant} $color={color} aria-hidden>
            <Icon />
          </TileIcon>
          <TileLabel>{label}</TileLabel>
          {variant === 'icon' ? <TileHint>{TRADE_EXAMPLE_JOBS[key]}</TileHint> : null}
        </TradeTile>
      ))}
    </TileRow>
  );
};

TradeTiles.propTypes = {
  variant: PropTypes.oneOf(['icon', 'pill']),
  /** Free text typed so far; carried to the next step as a note. */
  note: PropTypes.string,
};

export default TradeTiles;
